import React, { useState, useEffect } from "react";
import MainLayouts from "layouts/MainLayouts";
import checkList from "../../assets/images/icons/checklist.png";
import {
  addToCart,
  changeProductFromCart,
} from "features/common/layouts/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { formatIDR } from "helpers/utils";
import Spinner from "react-bootstrap/Spinner";
import { getProducts, getAddons } from "features/digitalSignature/digitalSignatureSlice";
import { notifySuccess } from "components/ToastMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

const DigitalSignatureOrder = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const product = useSelector((state) => state.digitalSignature);
  const { productsList } = useSelector((state) => state.cart);
  const [selectedDuration, setSelectedDuration] = useState("annually");
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    if (location.pathname === "/ordersignature") {
      dispatch(getProducts(81));
      dispatch(getAddons(81));
    }
  }, [dispatch, location]);

  // Update cart when addons change if product already in cart
  useEffect(() => {
    const checkSignature = productsList.find((list) => list.pid === 81);
    if (checkSignature && product.price) {
      dispatch(changeProductFromCart(createProductObject(selectedDuration)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddons]);

  // Helper function to get duration multiplier
  const getDurationMultiplier = (duration) => {
    switch (duration) {
      case "annually":
        return 1;
      case "biennially":
        return 2;
      case "triennially":
        return 3;
      default:
        return 1;
    }
  };

  // Helper function to create product object
  const createProductObject = (duration) => {
    const basePrice = +product.price[duration];
    const multiplier = getDurationMultiplier(duration);
    
    // Calculate addon price - check if one-time or recurring
    const addonsPrice = selectedAddons.reduce((total, addon) => {
      const isOneTime = addon.billingcycle && addon.billingcycle.toLowerCase() === "one time";
      const addonPrice = parseFloat(addon.monthly);
      // One-time addons don't multiply with duration
      return total + (isOneTime ? addonPrice : addonPrice * multiplier);
    }, 0);

    const productObj = {
      pid: 81,
      name: product.name,
      price: basePrice + addonsPrice,
      duration: duration,
      type: "other",
    };

    // Add addon if selected
    if (selectedAddons.length > 0) {
      productObj.addon = selectedAddons.map(addon => {
        const isOneTime = addon.billingcycle && addon.billingcycle.toLowerCase() === "one time";
        const baseAddonPrice = parseFloat(addon.monthly);
        const finalPrice = isOneTime ? baseAddonPrice : baseAddonPrice * multiplier;
        
        return {
          id: addon.id,
          name: addon.name,
          price: finalPrice,
          pricePerUnit: baseAddonPrice,
          billingcycle: addon.billingcycle,
          multiplier: isOneTime ? 1 : multiplier,
          isOneTime: isOneTime,
        };
      });
    }

    return productObj;
  };

  const handleClickProduct = (duration) => {
    const checkSignature = productsList.find((list) => list.pid === 81);
    setSelectedDuration(duration);
    
    if (checkSignature) {
      dispatch(changeProductFromCart(createProductObject(duration)));
    }
  };

  const handleAddonChange = (addon, isChecked) => {
    if (isChecked) {
      setSelectedAddons([...selectedAddons, addon]);
    } else {
      setSelectedAddons(selectedAddons.filter((a) => a.id !== addon.id));
    }
  };

  const handlerSendToCart = (e) => {
    e.preventDefault();
    dispatch(addToCart(createProductObject(selectedDuration)));
    notifySuccess("Produk berhasil ditambahkan ke keranjang.");
  };

  // Calculate total price
  const getTotalPrice = () => {
    const basePrice = product.price ? +product.price[selectedDuration] : 0;
    const multiplier = getDurationMultiplier(selectedDuration);
    const addonsPrice = selectedAddons.reduce((total, addon) => {
      const isOneTime = addon.billingcycle && addon.billingcycle.toLowerCase() === "one time";
      const addonPrice = parseFloat(addon.monthly);
      // One-time addons don't multiply with duration
      return total + (isOneTime ? addonPrice : addonPrice * multiplier);
    }, 0);
    return basePrice + addonsPrice;
  };

  // Duration options configuration
  const durationOptions = [
    { value: "annually", label: "1 Tahun", years: 1 },
    { value: "biennially", label: "2 Tahun", years: 2 },
    { value: "triennially", label: "3 Tahun", years: 3 },
  ];

  const isLoading = product.status === "loading";

  return (
    <MainLayouts summary={false}>
      <div 
        className="card mb-3" 
        style={{ 
          borderRadius: "16px", 
          borderColor: "transparent", 
          boxShadow: "0px 0px 4px rgba(190, 190, 190, 0.5)" 
        }}
      >
        {/* Header Section */}
        <div className="card-body">
          <div className="form-check py-1 px-4">
            <input
              type="radio"
              className="form-check-input"
              name="digitalSignature"
              id="digitalSignature"
              value="digitalSignature"
              defaultChecked={true}
            />
            <label
              htmlFor="digitalSignature"
              className="form-check-label fw-bold"
              style={{ fontSize: "18px", marginLeft: "20px" }}
            >
              Digital Signature
            </label>
            <p className="mb-0 mt-2 text-muted" style={{ marginLeft: "21px", fontSize: "14px" }}>
              Adobe Authorized Trust List Certificate are trusted across platforms
              and allow users to sign emails, Microsoft Office, and Adobe PDF
              Documents. Amankan dokumen Anda dengan tanda tangan digital, dan
              sederhanakan proses tanda tangan untuk dokumen Anda dengan Digital
              Signature dari Globalsign, hanya tersedia di Indonesia di
              Qwords.com.
            </p>
          </div>

          <hr className="my-3" />

          {/* Content Section */}
          <div className="px-3 px-md-4">
            {/* Duration Selection Label */}
            <label className="form-label fw-bold mb-3">Pilih Masa Aktif</label>

            {/* Loading State */}
            {isLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2 mb-0">Memuat data produk...</p>
              </div>
            ) : (
              <>
                {/* Duration Cards Grid */}
                <div className="row g-3 mb-4">
                  {durationOptions.map((option) => (
                    <div key={option.value} className="col-12 col-md-6 col-lg-4">
                      <div
                        className={`card position-relative h-100 ${
                          selectedDuration === option.value
                            ? "border-primary border-2"
                            : "border"
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleClickProduct(option.value)}
              >
                <div className="card-body">
                          <h5 className="mb-2 fw-bold">{option.label}</h5>
                          <div className="fw-bold text-qw-primary fs-6">
                            {formatIDR(product.price[option.value])}
                </div>
              </div>
                        
                        {/* Checkmark */}
                        {selectedDuration === option.value && (
                          <div
                            className="position-absolute"
                            style={{ right: "8px", top: "8px" }}
                          >
                            <img src={checkList} alt="selected" width="24" height="24" />
                          </div>
                        )}
                </div>
              </div>
                  ))}
            </div>

                {/* Product Specifications */}
                <div className="mt-4 mb-4">
                  <div className="align-items-start mb-1">
                    <FontAwesomeIcon icon={faCheck} className="text-success me-2 flex-shrink-0" style={{ marginTop: "2px" }} />
                    <span className="text-muted small">
                      <strong>Termasuk ongkos kirim seluruh Indonesia untuk dongle, dan manual instalasi</strong>
                    </span>
                  </div>

                  <div className="align-items-start mb-1">
                    <FontAwesomeIcon icon={faCheck} className="text-success me-2 flex-shrink-0" style={{ marginTop: "2px" }} />
                    <span className="text-muted small">
                      <strong>Termasuk garansi 6 Bulan untuk Dongle (untuk garansi akibat kesalahan pabrik dengan kondisi pengecualian : kehilangan, terkena air, tegangan tidak stabil, overheat, korslet, terjatuh, penyimpanan salah, dan kerusakan akibat pemakaian)</strong>
                    </span>
                  </div>

                  <div className="align-items-start mb-1">
                    <FontAwesomeIcon icon={faCheck} className="text-success me-2 flex-shrink-0" style={{ marginTop: "2px" }} />
                    <span className="text-muted small">
                      <strong>Biaya Replacement jika Dongle RUSAK/HILANG: Rp1.500.000 (Optional)</strong>
                    </span>
                  </div>
                </div>

                {/* Addons Section */}
                {product.addons && product.addons.length > 0 && (
                  <>
                    <div className="row mb-3 mt-5">
                      <div className="col-12">
                        <h6 className="fw-bold text-dark mb-0">Produk addons (Opsional)</h6>
                      </div>
                    </div>
                    {product.addons.map((addon) => {
                      const isOneTime = addon.billingcycle && addon.billingcycle.toLowerCase() === "one time";
                      const multiplier = getDurationMultiplier(selectedDuration);
                      const addonPrice = parseFloat(addon.monthly);
                      const totalAddonPrice = isOneTime ? addonPrice : addonPrice * multiplier;
                      
                      return (
                        <div key={addon.id} className="row my-4 justify-content-between align-items-start">
                          <div className="form-check col-12 col-lg-6">
                            <div className="flex-shrink-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`addon-${addon.id}`}
                                checked={selectedAddons.some((a) => a.id === addon.id)}
                                onChange={(e) => handleAddonChange(addon, e.target.checked)}
                                style={{ marginTop: "2px" }}
                              />
                            </div>
                            <div className="flex-grow-1 ms-2">
                              <label
                                htmlFor={`addon-${addon.id}`}
                                className="form-check-label fw-bold"
                                style={{ fontSize: "16px" }}
                              >
                                {addon.name}
                              </label>
                              {addon.description && (
                                <p className="text-muted small mb-0 mt-1">
                                  {addon.description}
                                </p>
                              )}
                              {isOneTime && (
                                <span className="badge bg-warning text-dark p-2 mt-2">Sekali Bayar</span>
                              )}
                              {!isOneTime && multiplier > 1 && (
                                <p className="text-muted small mb-0 mt-1">
                                  {formatIDR(addonPrice)} x {multiplier} tahun
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-12 col-lg-4 d-flex flex-column align-items-end mt-2 mt-lg-0">
                            <p className="mb-0 fw-bold text-qw-primary" style={{ fontSize: "18px" }}>
                              {formatIDR(totalAddonPrice)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Total Price */}
                {/* {selectedAddons.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                      <div className="fw-bold">Total Harga:</div>
                      <div className="fw-bold text-qw-primary fs-5">
                        {formatIDR(getTotalPrice())}
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Add to Cart Button */}
                <div className="text-center mt-5 mb-3">
                  <button
                    type="button"
                    className="btn btn-cta fw-bold py-2 px-5 rounded-5"
                    onClick={handlerSendToCart}
                    disabled={isLoading}
                  >
                    Tambahkan ke Keranjang
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayouts>
  );
};

export default DigitalSignatureOrder;
